import React from 'react';
import { useCursor } from '../context/CursorContext';
import { AnimatedSection, StaggerChildren } from '../components/AnimatedSection';
import { TiltCard } from '../components/TiltCard';
import { useYouTubeCreators, formatCount } from '../hooks/useYouTube';
import { creators as fallbackCreators } from '../data/mock';

export default function CreatorsPage() {
  const { setLabel } = useCursor();
  const { data: ytData, loading } = useYouTubeCreators();

  const creators = (ytData?.creators || fallbackCreators)
    .slice()
    .sort((a, b) => (b.subscriber_count || 0) - (a.subscriber_count || 0));

  return (
    <div className="min-h-screen pt-32 pb-20 px-6">
      <div className="max-w-5xl mx-auto">
        <AnimatedSection className="text-center mb-16">
          <p className="text-green-500 font-semibold uppercase tracking-[0.3em] mb-3 text-xs">
            Meet
          </p>
          <h1 className="text-4xl md:text-5xl font-black text-white mb-3">
            OUR CREATORS
          </h1>
          <p className="text-white/40">The best in crypto content</p>
          <div className="mt-4 mx-auto w-16 h-px bg-gradient-to-r from-transparent via-green-500 to-transparent" />
          {ytData?.cached_at && (
            <p className="text-white/20 text-[10px] mt-3 uppercase tracking-wider">
              Live data &middot; Updated {new Date(ytData.cached_at).toLocaleDateString()}
            </p>
          )}
        </AnimatedSection>

        <StaggerChildren className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" staggerDelay={120}>
          {creators.map((creator) => {
            const isLive = !!creator.channel_id;
            const name = creator.display_name || creator.name;
            const subs = isLive ? formatCount(creator.subscriber_count) : (creator.subscribers || '0');
            const isFeatured = creator.featured;
            const handle = creator.handle;
            const ytUrl = handle ? `https://youtube.com/@${handle}` : null;

            return (
              <TiltCard
                key={handle || name}
                cursorLabel="View"
                glowColor={isFeatured ? 'rgba(34, 197, 94, 0.2)' : 'rgba(34, 197, 94, 0.1)'}
                className={`p-6 relative ${
                  isFeatured
                    ? 'border border-green-500/30 bg-green-500/5 hover:border-green-500/50'
                    : 'border border-white/5 bg-white/[0.02] hover:border-green-500/30'
                }`}
              >
                {isFeatured && (
                  <span className="absolute top-4 right-4 px-2 py-0.5 bg-green-500 text-black text-[9px] font-bold uppercase">
                    Featured
                  </span>
                )}
                {creator.thumbnail ? (
                  <img
                    src={creator.thumbnail}
                    alt={name}
                    className="w-12 h-12 rounded-full mb-4 border border-green-500/30"
                  />
                ) : (
                  <div className={`w-12 h-12 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center mb-4 ${isFeatured ? 'animate-pulse-glow' : ''}`}>
                    <span className="text-lg font-black text-black">
                      {name[0]}
                    </span>
                  </div>
                )}
                <p className="text-[10px] text-green-500 uppercase tracking-wider">
                  {creator.category}
                </p>
                <h3 className="text-xl font-black text-white mt-1 mb-1">
                  {name}
                </h3>
                <p className="text-white/60 text-sm mb-1">
                  {subs} subscribers
                </p>
                {isLive && (
                  <p className="text-white/30 text-xs mb-2">
                    {formatCount(creator.view_count)} views &middot; {creator.video_count} videos
                  </p>
                )}
                <p className="text-white/40 text-sm">{creator.description}</p>
                {ytUrl && (
                  <a
                    href={ytUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block mt-3 text-green-500 text-xs font-semibold uppercase tracking-wider hover:text-green-400 transition-colors duration-300"
                    onMouseEnter={() => setLabel('YouTube')}
                    onMouseLeave={() => setLabel('')}
                  >
                    View Channel &rarr;
                  </a>
                )}
              </TiltCard>
            );
          })}
        </StaggerChildren>
      </div>
    </div>
  );
}
